import { oc } from "@orpc/contract";
import { z } from "zod";

// Natural-language → Vega-Lite spec generation for the report canvas's
// vega-chart component. The client owns validation (it compiles the returned
// spec with vega-lite) and retries once by echoing the failed attempt back
// through `previousAttempt`.

export const VegaSpecDatasetColumnSchema = z.object({
  key: z.string().min(1).max(200),
  label: z.string().max(200),
  /** 'number' columns are quantitative; everything else is categorical. */
  dataType: z.string().max(50),
});

const DatasetRowSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);

/** An open JSON object — the Vega-Lite grammar is too large to model here. */
const VegaSpecSchema = z.record(z.string(), z.unknown());

export const GenerateVegaSpecInputSchema = z.object({
  /** The user's natural-language description of the chart they want. */
  prompt: z.string().min(1).max(2000),
  datasetLabel: z.string().max(200).optional(),
  columns: z.array(VegaSpecDatasetColumnSchema).min(1).max(64),
  /** A few example rows so the model sees real value shapes. */
  sampleRows: z.array(DatasetRowSchema).max(5).optional(),
  /** When set, the request is an edit of this spec rather than a fresh chart. */
  currentSpec: VegaSpecSchema.optional(),
  /** Set on retry: the spec the model produced last time and why it failed. */
  previousAttempt: z
    .object({
      spec: VegaSpecSchema,
      error: z.string().max(4000),
    })
    .optional(),
});

export const GenerateVegaSpecResponseSchema = z.object({
  spec: VegaSpecSchema,
});

// ── Whole-canvas layout generation ────────────────────────────────────────────
// "Prompt the whole canvas": the user describes what the analytics section
// should show; the model returns a full serialized layout ({ data:
// component[][] }). Like the vega contract, layout shapes are DELIBERATELY
// loose records: the client owns validation (schema parse + registry checks +
// vega compile) and repairs via `previousAttempt` — a strict output schema
// here would 500 inside oRPC before the client's repair loop ever saw the
// failed attempt, and `previousAttempt.layout` is by definition a layout that
// failed the strict schema.

/** A serialized canvas layout as an open JSON object. */
const LooseLayoutSchema = z.record(z.string(), z.unknown());

export const CanvasGenerationDatasetSchema = z.object({
  key: z.string().min(1).max(200),
  label: z.string().max(200),
  columns: z.array(VegaSpecDatasetColumnSchema).max(64),
  /** A few example rows so the model sees real value shapes. */
  sampleRows: z.array(DatasetRowSchema).max(3).optional(),
});

export const CanvasGenerationComponentSchema = z.object({
  type: z.string().min(1).max(100),
  label: z.string().max(200),
  /** AI hint: what the widget shows, config guidance, dataset binding. */
  description: z.string().max(1000).optional(),
  /** The widget's make() defaults, minus identity fields (id/v). */
  defaultConfig: z.record(z.string(), z.unknown()),
});

export const GenerateCanvasLayoutInputSchema = z.object({
  /** The user's natural-language description of the desired layout. */
  prompt: z.string().min(1).max(2000),
  /** The layout being edited, serialized ({ data: component[][] }). */
  currentLayout: LooseLayoutSchema,
  datasets: z.array(CanvasGenerationDatasetSchema).min(1).max(10),
  /** The surface's registry catalog — the only component types allowed. */
  components: z.array(CanvasGenerationComponentSchema).min(1).max(32),
  /** Set on retry: the layout the model produced last time and why it failed. */
  previousAttempt: z
    .object({
      layout: LooseLayoutSchema,
      errors: z.string().max(4000),
    })
    .optional(),
});

export const GenerateCanvasLayoutResponseSchema = z.object({
  layout: LooseLayoutSchema,
});

export const reportCanvasAiContracts = {
  generateVegaSpec: oc
    .input(GenerateVegaSpecInputSchema)
    .output(GenerateVegaSpecResponseSchema)
    .route({ method: "POST" }),
  generateCanvasLayout: oc
    .input(GenerateCanvasLayoutInputSchema)
    .output(GenerateCanvasLayoutResponseSchema)
    .route({ method: "POST" }),
} as const;

export type GenerateVegaSpecInput = z.infer<typeof GenerateVegaSpecInputSchema>;
export type GenerateVegaSpecResponse = z.infer<
  typeof GenerateVegaSpecResponseSchema
>;
export type VegaSpecDatasetColumn = z.infer<typeof VegaSpecDatasetColumnSchema>;
export type GenerateCanvasLayoutInput = z.infer<
  typeof GenerateCanvasLayoutInputSchema
>;
export type GenerateCanvasLayoutResponse = z.infer<
  typeof GenerateCanvasLayoutResponseSchema
>;
export type CanvasGenerationDataset = z.infer<
  typeof CanvasGenerationDatasetSchema
>;
export type CanvasGenerationComponent = z.infer<
  typeof CanvasGenerationComponentSchema
>;
